import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Badge, Spinner, Empty, fmtBRL } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, X, Pencil, Trash2, RefreshCw, Power } from "lucide-react";

const empty = { nome: "", tipo: "a_pagar", valor: "", dia_vencimento: 5, counterpart: "", categoria: "outros", ativo: true, observacoes: "" };

export default function Recurring() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [genResult, setGenResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/recurring"); setItems(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); setErr(""); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setOpen(true); setErr(""); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form, valor: Number(form.valor) || 0, dia_vencimento: Number(form.dia_vencimento) || 1 };
      if (editing) await api.put(`/finance/recurring/${editing.id}`, payload);
      else await api.post("/finance/recurring", payload);
      setOpen(false); await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Excluir esta recorrência?")) return;
    try { await api.delete(`/finance/recurring/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };
  const toggleAtivo = async (r) => {
    try { await api.put(`/finance/recurring/${r.id}`, { ...r, ativo: !r.ativo }); await load(); }
    catch (e) { alert(formatErr(e)); }
  };
  const generate = async () => {
    if (!window.confirm("Gerar contas a pagar/receber para o mês corrente? (idempotente)")) return;
    setBusy(true); setErr("");
    try { const { data } = await api.post("/finance/recurring/generate"); setGenResult(data); await load(); }
    catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <Layout
      title="Lançamentos Recorrentes"
      subtitle="Modelos de contas que se repetem mensalmente (aluguel, energia, etc)"
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={generate} disabled={busy} data-testid="generate-recurring-button"><RefreshCw size={16} /> Gerar mês corrente</Button>
          <Button onClick={openNew} data-testid="new-recurring-button"><Plus size={16} /> Nova recorrência</Button>
        </div>
      }
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}
      {genResult && (
        <Card className="p-4 mb-4 bg-emerald-50 border-emerald-200">
          <div className="text-sm">
            <span className="font-semibold text-ok">Período {genResult.period}:</span>{" "}
            <span className="text-ok">{genResult.created.length} criadas</span>
            {genResult.skipped.length > 0 && <span className="text-ink-500">, {genResult.skipped.length} ignoradas (já existiam)</span>}
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : items.length === 0 ? (
        <Card><Empty title="Nenhuma recorrência cadastrada" hint="Cadastre despesas fixas como aluguel ou energia." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-tertiary">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3 text-center">Dia</th>
                <th className="px-4 py-3">Beneficiário/Cliente</th>
                <th className="px-4 py-3">Última geração</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-tertiary last:border-0 hover:bg-stone-50 transition-colors" data-testid={`recurring-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-ink-900">{r.nome}</td>
                  <td className="px-4 py-3"><Badge tone={r.tipo === "a_pagar" ? "warn" : "success"}>{r.tipo === "a_pagar" ? "A pagar" : "A receber"}</Badge></td>
                  <td className="px-4 py-3 capitalize text-ink-700">{r.categoria}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmtBRL(r.valor)}</td>
                  <td className="px-4 py-3 font-mono text-center">{r.dia_vencimento}</td>
                  <td className="px-4 py-3 text-ink-700">{r.counterpart || "—"}</td>
                  <td className="px-4 py-3 font-mono text-ink-500">{r.last_generated_period || "—"}</td>
                  <td className="px-4 py-3">{r.ativo ? <Badge tone="success">Ativo</Badge> : <Badge tone="neutral">Pausado</Badge>}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => toggleAtivo(r)} className="p-1.5 rounded-md hover:bg-stone-100 text-ink-700" title={r.ativo ? "Pausar" : "Ativar"}><Power size={16} /></button>
                    <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-stone-100 text-ink-700" title="Editar"><Pencil size={16} /></button>
                    <button onClick={() => onDelete(r.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" title="Excluir"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-tertiary">
              <h3 className="font-heading font-bold text-ink-900">{editing ? "Editar recorrência" : "Nova recorrência"}</h3>
              <button onClick={() => setOpen(false)} className="text-ink-500"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <Input label="Nome" required value={form.nome} onChange={set("nome")} data-testid="recurring-nome" />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Tipo" value={form.tipo} onChange={set("tipo")}>
                  <option value="a_pagar">A pagar</option>
                  <option value="a_receber">A receber</option>
                </Select>
                <Select label="Categoria" value={form.categoria} onChange={set("categoria")}>
                  <option value="outros">Outros</option>
                  <option value="aluguel">Aluguel</option>
                  <option value="energia">Energia</option>
                  <option value="agua">Água</option>
                  <option value="internet">Internet</option>
                  <option value="folha">Folha</option>
                  <option value="salario_adm">Salários ADM</option>
                  <option value="impostos">Impostos</option>
                  <option value="venda">Venda</option>
                  <option value="manutencao">Manutenção</option>
                  <option value="materiais_escritorio">Materiais de Escritório</option>
                  <option value="materiais_limpeza">Materiais de Limpeza</option>
                  <option value="taxas_bancarias">Taxas Bancárias</option>
                  <option value="cesta_basica">Cesta Básica</option>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Valor (R$)" type="number" step="0.01" required value={form.valor} onChange={set("valor")} data-testid="recurring-valor" />
                <Input label="Dia do vencimento" type="number" min="1" max="31" required value={form.dia_vencimento} onChange={set("dia_vencimento")} data-testid="recurring-dia" />
              </div>
              <Input label="Beneficiário / Cliente" value={form.counterpart} onChange={set("counterpart")} />
              <Input label="Observações" value={form.observacoes} onChange={set("observacoes")} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.ativo} onChange={set("ativo")} className="accent-brand-500" />
                <span>Ativo (será gerado a cada mês)</span>
              </label>
              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="recurring-submit">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
